/**
 * Safe zone of a photo: the rectangle (fractions of the image, 0..1) that must
 * stay visible whichever frame the photo is cropped to. `ar` is the image's
 * width / height, which cover-cropping needs to place the window.
 */
export type SafeZone = {
	x: number
	y: number
	w: number
	h: number
	ar: number
}

const MIN_SIDE = 0.05

const clamp01 = (n: number) => Math.min(1, Math.max(0, n))

/** Parse the stored JSON (or a form value); null when empty or malformed. */
export function parseSafeZone(raw: unknown): SafeZone | null {
	if (typeof raw !== 'string' || raw.trim() === '') return null
	let value: unknown
	try {
		value = JSON.parse(raw)
	} catch {
		return null
	}
	if (typeof value !== 'object' || value === null) return null
	const { x, y, w, h, ar } = value as Record<string, unknown>
	const nums = [x, y, w, h, ar]
	if (!nums.every((n) => typeof n === 'number' && Number.isFinite(n)))
		return null
	const zone = { x, y, w, h, ar } as SafeZone
	if (zone.ar <= 0) return null
	const cx = clamp01(zone.x)
	const cy = clamp01(zone.y)
	const cw = Math.min(1 - cx, zone.w)
	const ch = Math.min(1 - cy, zone.h)
	if (cw < MIN_SIDE || ch < MIN_SIDE) return null
	return { x: cx, y: cy, w: cw, h: ch, ar: zone.ar }
}

export function serializeSafeZone(zone: SafeZone | null): string | null {
	return zone ? JSON.stringify(zone) : null
}

/**
 * Where a cover-cropped photo's window sits (CSS object-position percentages)
 * so the zone is centred in the frame, and whether the whole zone fits.
 */
export function coverWindow(zone: SafeZone | null, frameAspect: number) {
	if (!zone) return { xPct: 50, yPct: 50, fits: true }
	// fraction of the image still visible on each axis after cover scaling
	const visX = zone.ar > frameAspect ? frameAspect / zone.ar : 1
	const visY = zone.ar < frameAspect ? zone.ar / frameAspect : 1
	const place = (start: number, size: number, vis: number) => {
		if (vis >= 1) return { pct: 50, fits: true }
		const centre = start + size / 2
		const from = Math.min(1 - vis, Math.max(0, centre - vis / 2))
		return {
			pct: (from / (1 - vis)) * 100,
			fits: start >= from - 1e-6 && start + size <= from + vis + 1e-6,
		}
	}
	const px = place(zone.x, zone.w, visX)
	const py = place(zone.y, zone.h, visY)
	return { xPct: px.pct, yPct: py.pct, fits: px.fits && py.fits }
}

/** Inline style for an <img> filled with object-fit: cover. */
export function safeZoneImageStyle(
	zone: SafeZone | null,
	frameAspect: number,
): { objectPosition: string } | undefined {
	if (!zone) return undefined
	const { xPct, yPct } = coverWindow(zone, frameAspect)
	return { objectPosition: `${xPct.toFixed(1)}% ${yPct.toFixed(1)}%` }
}

/**
 * The zone submitted next to the photo (`photoSafeZone`): the JSON to store,
 * `null` (cleared, or no photo any more), or `undefined` when the form didn't
 * carry the field at all (keep what's stored).
 */
export function resolveSafeZonePatch(formData: FormData) {
	if (!formData.has('photoSafeZone')) return undefined
	return serializeSafeZone(parseSafeZone(formData.get('photoSafeZone')))
}
